/**
 * Inline rename for a conversation title (shell_metadata.title).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { PencilIcon, CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';

interface ConversationTitleEditorProps {
  title: string;
  onSave: (nextTitle: string) => Promise<void>;
  className?: string;
  /** Compact row for sidebar lists. */
  variant?: 'header' | 'inline';
}

export const ConversationTitleEditor: React.FC<ConversationTitleEditorProps> = ({
  title,
  onSave,
  className = '',
  variant = 'header',
}) => {
  const { t } = useLanguage();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  const [saving, setSaving] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);

  useEffect(() => {
    if (!editing) setDraft(title);
  }, [title, editing]);

  const cancel = useCallback(() => {
    setDraft(title);
    setEditing(false);
    setErrorKey(null);
  }, [title]);

  const save = useCallback(async () => {
    const next = draft.trim();
    if (!next) {
      setErrorKey('aiStudio.conversation.renameRequired');
      return;
    }
    if (next === title.trim()) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setErrorKey(null);
    try {
      await onSave(next);
      setEditing(false);
    } catch {
      setErrorKey('aiStudio.conversation.renameFailed');
    } finally {
      setSaving(false);
    }
  }, [draft, onSave, title]);

  if (editing) {
    return (
      <div className={`flex flex-col gap-1 ${className}`}>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={255}
            disabled={saving}
            className="min-w-[12rem] flex-1 rounded border border-[var(--color-border)] bg-[var(--color-surface-bg)] px-2 py-1 text-base text-[var(--color-surface-text)]"
            placeholder={t('aiStudio.conversation.renamePlaceholder')}
            aria-label={t('aiStudio.conversation.renamePlaceholder')}
          />
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving}
            className="rounded border border-[var(--color-border)] p-1 text-[var(--color-surface-text)] hover:bg-[var(--color-secondary-bg)] disabled:opacity-50"
            title={t('aiStudio.conversation.renameSave')}
          >
            <CheckIcon className="h-4 w-4" aria-hidden />
          </button>
          <button
            type="button"
            onClick={cancel}
            disabled={saving}
            className="rounded border border-[var(--color-border)] p-1 text-[var(--color-surface-text)] hover:bg-[var(--color-secondary-bg)] disabled:opacity-50"
            title={t('aiStudio.conversation.renameCancel')}
          >
            <XMarkIcon className="h-4 w-4" aria-hidden />
          </button>
        </div>
        {errorKey && (
          <p className="text-sm text-[var(--color-error)]" role="alert">
            {t(errorKey)}
          </p>
        )}
      </div>
    );
  }

  const titleClass =
    variant === 'header'
      ? 'text-base font-medium text-[var(--color-surface-text)]'
      : 'truncate text-base font-medium text-[var(--color-surface-text)]';

  return (
    <div className={`flex min-w-0 items-center gap-2 ${className}`}>
      <span className={`min-w-0 truncate ${titleClass}`} title={title}>
        {title}
      </span>
      <button
        type="button"
        onClick={() => setEditing(true)}
        className="shrink-0 rounded p-1 text-[var(--color-info)] hover:bg-[var(--color-secondary-bg)]"
        title={t('aiStudio.conversation.rename')}
        aria-label={t('aiStudio.conversation.rename')}
      >
        <PencilIcon className="h-4 w-4" aria-hidden />
      </button>
    </div>
  );
};
